import { selectCurrencyCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';

import { useAssetPriceContents } from '@/hooks/asset-price-contents.hooks';
import { useUniqueSavings } from '@/hooks/asset-price-savings.hooks';
import { useAssetPriceTotals } from '@/hooks/asset-price-totals.hooks';
import { useAssetServices } from '@/hooks/asset-services.hooks';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { formatPrice } from '@/utils/translation.utils';

import type { PriceSummaryEntry } from './price-summary-overview.types';

export const usePriceSummaryOverviewContentsEntries = (): PriceSummaryEntry[] => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const priceContents = useAssetPriceContents();
    const savings = useUniqueSavings();
    const { totalItemsValue } = useAssetPriceTotals();
    const currencyCode = selectCurrencyCode(data);
    const services = useAssetServices(data);

    const mapSavingToEntry = ({
        description,
        id,
        savingValue,
    }: {
        description: string | undefined;
        id: string;
        savingValue: number;
    }) => ({
        description,
        id,
        reducesPrice: true,
        shouldRender: savingValue !== 0,
        value: formatPrice(savingValue, currencyCode, {
            forceFixed: false,
        }),
    });

    return [
        {
            description: translate('asset_price_summary_items'),
            id: 'asset_price_summary_items',
            shouldRender: totalItemsValue > 0,
            value: formatPrice(totalItemsValue, currencyCode, {
                forceFixed: false,
            }),
        },
        {
            description: translate('asset_price_summary_delivery'),
            id: 'asset_price_summary_delivery',
            shouldRender: services.some((service) => service.isDelivery),
            value: formatPrice(priceContents.delivery, currencyCode, {
                forceFixed: false,
            }),
        },
        {
            description: translate('asset_price_summary_services'),
            id: 'asset_price_summary_services',
            shouldRender: services.some((service) => !service.isDelivery),
            value: formatPrice(priceContents.services, currencyCode, {
                forceFixed: false,
            }),
        },
        ...savings.map(mapSavingToEntry),
    ];
};
